import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import {
  EXPERIMENT_CARD_BADGE_HEIGHT,
  EXPERIMENT_CARD_BADGE_WIDTH
} from 'dpl/breeder_preferred_qualifications/utils/constants';
import CardImagePlaceholder from 'dpl/breeder_search_results/components/CardImagePlaceholder';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import CertifiedBreederBadge from 'dpl/components/BreederProfile/mobile/components/CertifiedBreederBadge';
import { MOBILE_LAYOUTS } from 'dpl/components/BreederProfile/utils/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import {
  FILTER_LOCATION_NAME,
  MOBILE_LAYOUT
} from 'dpl/constants/query_params';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import useSimilarBreedFilter from 'dpl/puppy_search_results/hooks/useSimilarBreedFilter';
import { makeSequence } from 'dpl/shared/utils';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { addToQuery } from 'dpl/util/queryString';

import useBreederAvailability from '../../hooks/useBreederAvailability';
import useFeaturedTestimonial from '../../hooks/useFeaturedTestimonial';
import useResultMedia from '../../hooks/useResultMedia';
import { getDistanceTransportAnalyticsProps } from '../../utils/analytics';
import BreedAvailability from '../../components/BreedAvailability';
import FeaturedReview from '../../components/FeaturedReview';
import ReviewsCard from '../../components/ReviewsCard';
import CardMediaCarousel from './CardMediaCarousel';

const IS_LT_MD = isLessThanBreakpoint('md');
const IS_LT_LG = isLessThanBreakpoint('lg');
const CARD_PHOTO_SIZE = IS_LT_LG ? '240px' : '260px';
const CARD_VIDEO_WIDTH = IS_LT_MD ? '140px' : '150px';

const SMART_IMAGE_PROPS = {
  Tag: 'div',
  className: 'bg-center cover',
  crop: true,
  loadingClass: 'bg-light-gray',
  height: CARD_PHOTO_SIZE,
  width: CARD_PHOTO_SIZE,
  allowContextMenu: true
};

export function getBreederProfileUrl(url, params = {}) {
  const separator = url.includes('#') ? '&' : '#';
  const query = addToQuery(removeEmptyValues(params));

  return [url, query].join(separator);
}

const cardDefaultPropTrackInView = () => {};
/** @param {any} props */
export default function Card({
  position = null,
  profile,
  analytics,
  onFavoriteChange = null,
  trackInView = cardDefaultPropTrackInView
}) {
  const locationTitle = useSelector(({ server }) => server.locationTitle);

  const locName = useSelector(
    ({ queryParams }) => queryParams[FILTER_LOCATION_NAME]
  );

  const {
    cropped_photo_url: croppedPhotoUrl,
    name,
    display_name: displayName,
    first_name: breederFirstName,
    description,
    distance_in_miles: distance,
    profile_photo_url: profilePhotoUrl,
    snoozed,
    url,
    breeder_breed_id: breederBreedId,
    breed_id: breedId,
    breed,
    id: breederProfileId,
    testimonials_count: testimonialsCount,
    percent_recommend: percentRecommend,
    preferred_qualification: preferredQualification,
    distance_description: distanceDescription
  } = profile;

  // With the toggle on, every card names its breed - the searched one included -
  // because the feed no longer holds a single breed for the heading to speak for.
  const { isMixed: isSimilarBreedsMixed } = useSimilarBreedFilter();
  const breedTag = isSimilarBreedsMixed ? breed?.name : null;

  // Start loading async card data ~2 viewport-heights ahead of the card,
  // matching the puppy card. Bots/tests bypass the gate so prerender and
  // Jest still fetch.
  const [cardRef, isCardNearViewport] = useInView({
    triggerOnce: true,
    rootMargin: `${window.outerHeight * 2}px`
  });
  const shouldLoadAsyncCardData =
    !snoozed && (isCardNearViewport || CONFIG?.isTest || window.__DPL_BOT_UA);

  const {
    resultPhotos,
    resultVideo,
    isLoaded: isMediaLoaded
  } = useResultMedia({
    breedId,
    breederProfileId,
    enabled: shouldLoadAsyncCardData
  });

  // Snoozed breeders always render zero availability, so their fetch is
  // skipped entirely and they count as loaded.
  const {
    availablePuppyCount,
    availableAdultDogsCount,
    formattedUpcomingLittersDueDates,
    analyticsAvailabilityInfo,
    isLoaded: isAvailabilityFetchLoaded
  } = useBreederAvailability({
    breedId,
    breederProfileId,
    enabled: shouldLoadAsyncCardData
  });
  const isAvailabilityLoaded = snoozed || isAvailabilityFetchLoaded;

  const { featuredTestimonial } = useFeaturedTestimonial({
    breederProfileId,
    enabled: shouldLoadAsyncCardData
  });

  const mediaFiles = useMemo(() => {
    if (isEmpty(resultPhotos)) return [];

    const photosWithAlt = resultPhotos.map(photo => {
      return {
        ...photo,
        alt:
          photo.alt_text ||
          (locationTitle ? `${name} in ${locationTitle}` : name)
      };
    });

    const [firstPhoto, ...otherPhotos] = photosWithAlt;

    return [firstPhoto, resultVideo, ...otherPhotos].filter(Boolean);
  }, [resultPhotos, resultVideo, locationTitle, name]);

  const displayPhotoUrl = croppedPhotoUrl || profilePhotoUrl;

  const breederProfileUrl = getBreederProfileUrl(url, {
    [MOBILE_LAYOUT]: MOBILE_LAYOUTS.NEW,
    locName
  });

  const currentUser = useCurrentUser();
  const { isBuyer, isLoggedIn } = currentUser;

  const favoriteAnalytics = {
    ...analytics,
    breed_id: breedId,
    breeder_name: name,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    page_number: analytics.page_number || null,
    position: analytics.position || null,
    puppies_available: availablePuppyCount
  };

  const shouldShowFavoriteButton = !isLoggedIn || isBuyer;
  const shouldShowReviewsCard = !IS_LT_MD && !isEmpty(featuredTestimonial);

  const computedAnalytics = {
    ...analytics,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    breed_id: breedId,
    miles_away: distance,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD,
    ...getDistanceTransportAnalyticsProps(profile),
    availability_displayed: analyticsAvailabilityInfo || {
      puppies: 0,
      upcoming_litters: 0,
      adult_dogs: 0,
      reviews: testimonialsCount
    },
    reviews_score: percentRecommend
  };

  function handleClick() {
    sendRudderstackEvent(
      ANALYTICS_EVENTS.CARD_CLICKED_BY_USER,
      computedAnalytics
    );
  }

  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: inView => {
      if (inView) trackInView();
    }
  });

  function renderMedia() {
    if (!isEmpty(mediaFiles)) {
      return (
        <div className="BreederSearchResultsItem__image w-100 h-100 br3 relative z-0">
          <CardMediaCarousel
            mediaFiles={mediaFiles}
            smartImageProps={SMART_IMAGE_PROPS}
            muxVideoProps={{
              loop: true,
              muted: true,
              style: {
                width: CARD_VIDEO_WIDTH,
                '--media-object-fit': 'cover'
              }
            }}
            analytics={{
              ...computedAnalytics,
              breeder_preferred_status: undefined
            }}
            showCaptions
          />
        </div>
      );
    }

    return (
      <div className="flex gap-8 overflow-hidden pl4">
        {makeSequence(2).map(index =>
          isMediaLoaded ? (
            <CardImagePlaceholder key={index} size={CARD_PHOTO_SIZE} />
          ) : (
            <SkeletonBlock
              key={index}
              className="flex-none br4"
              width={CARD_PHOTO_SIZE}
              height={CARD_PHOTO_SIZE}
            />
          )
        )}
      </div>
    );
  }

  return (
    <a
      ref={ref}
      className="BreederSearchResultsCard h-100 db"
      href={breederProfileUrl}
      onClick={handleClick}
      draggable="false"
    >
      <div
        className={classnames(
          'BreederSearchResultsCard__container flex flex-column gap-16 bg-white h-100 b--default pv5',
          IS_LT_MD && position !== 1 && 'bt',
          IS_LT_MD ? 'ph5 bb' : 'br12 ba'
        )}
      >
        {!snoozed && (
          <div ref={cardRef} className="flex overflow-hidden nh5 nh0-md">
            {renderMedia()}
          </div>
        )}
        <div className="flex gap-16 ph5-md justify-between">
          <div className="flex-auto flex flex-column gap-16">
            <div className="flex items-center gap-8 md:gap-16">
              <div className="relative">
                <UserAvatar
                  user={{
                    id: profile.user_id,
                    initials: profile.initials,
                    profile_photo_url: displayPhotoUrl
                  }}
                  size="xxm"
                />
                <div className="absolute right-0" style={{ bottom: -2 }}>
                  <CertifiedBreederBadge
                    height={EXPERIMENT_CARD_BADGE_HEIGHT}
                    width={EXPERIMENT_CARD_BADGE_WIDTH}
                  />
                </div>
              </div>
              <div className="flex-auto flex gap-16">
                <div className="flex-auto">
                  <p className="font-serif fw-medium lh-title font-18 md:font-20 truncate">
                    Raised by {displayName}
                  </p>
                  <p className="flex items-center gap-4 lh-normal font-12 md:font-14 secondary">
                    <span className="hidden md:block truncate">{name}</span>
                    <span className="hidden md:block"> · </span>
                    <span className="font-12 md:font-14 secondary truncate">
                      {distanceDescription}
                    </span>
                  </p>
                </div>
                {IS_LT_MD && shouldShowFavoriteButton && (
                  <FavoriteButton
                    analytics={favoriteAnalytics}
                    followableTypeId={breederBreedId}
                    followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
                    onChange={onFavoriteChange}
                    checkedIconProps={{ name: 'fetch-heart-full-breeder' }}
                    uncheckedIconProps={{
                      name: 'fetch-heart',
                      className: 'secondary'
                    }}
                    breederFirstName={breederFirstName}
                    breederProfilePhotoUrl={profilePhotoUrl}
                  />
                )}
              </div>
            </div>
            <p className="font-14 md:font-18 secondary">{description}</p>
          </div>
          {!IS_LT_MD && (shouldShowFavoriteButton || shouldShowReviewsCard) && (
            <div className="flex flex-column items-end gap-8">
              {shouldShowFavoriteButton && (
                <FavoriteButton
                  analytics={favoriteAnalytics}
                  followableTypeId={breederBreedId}
                  followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
                  onChange={onFavoriteChange}
                  checkedIconProps={{ name: 'fetch-heart-full-breeder' }}
                  uncheckedIconProps={{
                    name: 'fetch-heart',
                    className: 'secondary'
                  }}
                />
              )}
              {shouldShowReviewsCard && (
                <ReviewsCard
                  reviewCount={testimonialsCount}
                  photographs={featuredTestimonial?.photographs_attributes}
                  percentRecommend={percentRecommend}
                />
              )}
            </div>
          )}
        </div>
        <BreedAvailability
          className={classnames('mt-auto', { nh5: IS_LT_MD })}
          breedName={breedTag}
          availablePuppyCount={availablePuppyCount}
          availableAdultDogsCount={availableAdultDogsCount}
          formattedUpcomingLittersDueDates={
            formattedUpcomingLittersDueDates || []
          }
          splideOptions={{ padding: 20 }}
          isLoading={!isAvailabilityLoaded}
        />
        {IS_LT_MD && !isEmpty(featuredTestimonial) && (
          <FeaturedReview
            reviewCount={testimonialsCount}
            sentence={featuredTestimonial.sentence}
            photographs={featuredTestimonial.photographs_attributes}
            percentRecommend={percentRecommend}
          />
        )}
      </div>
    </a>
  );
}

Card.propTypes = {
  position: PropTypes.number,
  profile: PropTypes.shape({
    cropped_photo_url: PropTypes.string,
    description: PropTypes.string,
    snoozed: PropTypes.bool,
    name: PropTypes.string,
    display_name: PropTypes.string,
    first_name: PropTypes.string,
    initials: PropTypes.string.isRequired,
    distance_in_miles: PropTypes.number,
    distance_description: PropTypes.string,
    transport_types: PropTypes.arrayOf(PropTypes.string),
    profile_photo_url: PropTypes.string,
    url: PropTypes.string.isRequired,
    breeder_breed_id: PropTypes.number,
    id: PropTypes.number,
    user_id: PropTypes.number.isRequired,
    breed_id: PropTypes.number,
    breed: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string
    }),
    testimonials_count: PropTypes.number,
    percent_recommend: PropTypes.number,
    preferred_qualification: PropTypes.shape({
      status: PropTypes.string
    }),
    state_slug: PropTypes.string,
    distance_analytics_props: PropTypes.shape({
      miles_away_displayed: PropTypes.number,
      logistics_display_type: PropTypes.string
    })
  }).isRequired,
  analytics: PropTypes.shape({
    page_number: PropTypes.number,
    position: PropTypes.number,
    view: PropTypes.string
  }).isRequired,
  onFavoriteChange: PropTypes.func,
  trackInView: PropTypes.func
};
